// frontend/src/components/TeamSwitcher.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../services/supabaseClient';
import FlagDisplay from './FlagDisplay';

const TeamSwitcher = ({ eventId, eventCode, currentTeamName }) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTeams = async () => {
            if (!eventId) return;

            const { data, error } = await adminService.getTeamsForEvent(eventId);
            if (!error && data) {
                setTeams(data);
            }
            setLoading(false);
        };
        fetchTeams();
    }, [eventId]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.team-switcher')) {
                setIsOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleTeamChange = (teamName) => {
        // Convert team name to URL-friendly format
        const urlTeamName = teamName.replace(/\s+/g, '-');
        navigate(`/admin/${eventCode}/${urlTeamName}`);
        setIsOpen(false);
    };

    if (loading || teams.length <= 1) {
        return null; // Don't show if loading or only one team
    }

    return (
        <div className="team-switcher relative">
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm"
            >
                <span className="text-indigo-200">Team:</span>
                <span className="font-medium flex items-center gap-1">
                    <FlagDisplay teamName={currentTeamName} size="sm" />
                    {currentTeamName}
                </span>
                <svg
                    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[220px]">
                    <div className="py-1 max-h-72 overflow-y-auto">
                        {teams.map((team) => (
                            <button
                                key={team.id}
                                onClick={() => handleTeamChange(team.name)}
                                className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 transition-colors ${currentTeamName === team.name
                                        ? 'bg-indigo-50 text-indigo-600 font-semibold'
                                        : 'text-gray-700'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <FlagDisplay teamName={team.name} size="sm" />
                                        {team.name}
                                    </span>
                                    {currentTeamName === team.name && (
                                        <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Back to Dashboard link */}
                    <div className="border-t border-gray-200 bg-gray-50">
                        <button
                            onClick={() => navigate(`/admin/${eventCode}`)}
                            className="w-full text-left px-4 py-3 text-sm text-indigo-600 hover:bg-gray-100 font-medium flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                            </svg>
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamSwitcher;
